import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  InputAdornment,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client/react";
import { CREATE_OFFER, UPDATE_OFFER } from "../graphql/mutation";
import { GET_OFFER_BY_ID, GET_PASS_LIST, GET_OFFER_LIST, GET_DASHBOARD_COUNTS } from "../graphql/query";
import themestyle from "../theme";
import CustomSnackbar from "./Snackbar";
import AppDatePicker from "./AppDatePicker";
import { FormLabel, textFieldSX } from "../utils/formField";
import type {
  CreateOfferMutationResponse,
  GetOfferByIdResponse,
  OfferFormMode,
  OfferInput,
  OfferPassType,
  UpdateOfferMutationResponse,
} from "../interfaces/offer.interface";

const PASS_TYPE_OPTIONS: Array<{ label: string; value: OfferPassType }> = [
  { label: "Drop-in", value: "DROP_IN" },
  { label: "Weekly", value: "WEEKLY_4_CLASSES" },
  { label: "Monthly", value: "MONTHLY_4_CLASSES" },
];

type AddOfferFormProps = {
  mode?: OfferFormMode;
  id?: string;
  onSuccess?: () => void;
  onCancel?: () => void;
  onEdit?: () => void;
};

const AddOfferForm: React.FC<AddOfferFormProps> = ({
  mode = "add",
  id,
  onSuccess,
  onCancel,
  onEdit,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const [formData, setFormData] = useState<OfferInput>({
    offerName: "",
    offerCode: "",
    validFrom: "",
    validTill: "",
    discount: "",
    passType: "DROP_IN",
    applicablePasses: [],
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarType, setSnackbarType] = useState<"success" | "error">(
    "success",
  );

  const [currentGlobalDate, setCurrentGlobalDate] = useState<string>("");

  useEffect(() => {
    const fetchGlobalTime = async () => {
      try {
        const response = await fetch(
          "https://worldtimeapi.org/api/timezone/Etc/UTC",
        );
        const data = await response.json();
        if (data && data.datetime) {
          const globalDate = data.datetime.split("T")[0];
          setCurrentGlobalDate(globalDate);
        }
      } catch (error) {
        console.error(error);
        const today = new Date().toISOString().split("T")[0];
        setCurrentGlobalDate(today);
      }
    };
    fetchGlobalTime();
  }, []);

  const isViewMode = mode === "view";

  const { data: offerData } = useQuery<GetOfferByIdResponse, { id: string }>(
    GET_OFFER_BY_ID,
    {
      variables: { id: id || "" },
      skip: mode === "add" || !id,
      fetchPolicy: "network-only",
    },
  );

  const { data: passesData } = useQuery(GET_PASS_LIST, {
    variables: { page: 1, perPage: 100, search: "" },
    fetchPolicy: "network-only",
  });

  const [createOffer, { loading: createLoading }] = useMutation<
    CreateOfferMutationResponse,
    { input: OfferInput }
  >(CREATE_OFFER, {
    refetchQueries: [
      { query: GET_OFFER_LIST, variables: { page: 1, perPage: 3 } },
      { query: GET_OFFER_LIST, variables: { page: 1, perPage: 10 } },
      { query: GET_DASHBOARD_COUNTS },
    ],
    awaitRefetchQueries: true,
  });

  const [updateOffer, { loading: updateLoading }] = useMutation<
    UpdateOfferMutationResponse,
    { id: string; input: OfferInput }
  >(UPDATE_OFFER, {
    refetchQueries: [
      { query: GET_OFFER_LIST, variables: { page: 1, perPage: 3 } },
      { query: GET_OFFER_LIST, variables: { page: 1, perPage: 10 } },
      { query: GET_DASHBOARD_COUNTS },
    ],
    awaitRefetchQueries: true,
  });

  const loading = createLoading || updateLoading;

  useEffect(() => {
    if (offerData?.getOfferById?.success && offerData.getOfferById.data) {
      const existing = offerData.getOfferById.data;
      setFormData({
        offerName: existing.offerName || "",
        offerCode: existing.offerCode || "",
        validFrom: existing.validFrom || "",
        validTill: existing.validTill || "",
        discount: Number(existing.discount) || 0,
        passType: existing.passType || "DROP_IN",
        applicablePasses: existing.applicablePasses || [],
      });
    }
  }, [offerData]);

  type PassOption = { name: string; cost: number };
  const OFFER_PASS_TYPE_TO_LABEL: Record<string, string> = {
    DROP_IN: "Drop-in",
    WEEKLY_4_CLASSES: "Weekly",
    MONTHLY_4_CLASSES: "Monthly",
  };

  const activePassOptions = useMemo((): PassOption[] => {
    const list = passesData?.getPassList?.data || [];
    const passTypeLabel = OFFER_PASS_TYPE_TO_LABEL[formData.passType] ?? formData.passType;
    return list
      .filter(
        (pass: any) =>
          pass.status === "Active" && pass.passType === passTypeLabel,
      )
      .map((pass: any) => ({ name: pass.passName as string, cost: pass.passCost as number }));
  }, [passesData, formData.passType]);

  const selectedPassDetails = useMemo((): PassOption[] => {
    return activePassOptions.filter((opt: PassOption) =>
      formData.applicablePasses.includes(opt.name),
    );
  }, [activePassOptions, formData.applicablePasses]);

  const validateField = (name: string, value: string | number | string[]) => {
    if (name === "offerName") {
      const text = String(value || "").trim();
      if (!text) return "Offer name is required";
      if (text.length < 2) return "Offer name must be at least 2 characters";
      return "";
    }

    if (name === "offerCode") {
      const text = String(value || "").trim();
      if (!text) return "Offer code is required";
      if (!/[a-zA-Z0-9]/.test(text)) return "Offer code cannot contain only special characters";
      return "";
    }

    if (name === "validFrom") {
      const date = String(value || "").trim();
      if (!date) return "Valid from date is required";
      return "";
    }

    if (name === "validTill") {
      const date = String(value || "").trim();
      if (!date) return "Valid till date is required";
      if (formData.validFrom && date < formData.validFrom) {
        return "Valid till date cannot be before valid from date";
      }
      return "";
    }

    if (name === "discount") {
      const number = Number(value);
      if (Number.isNaN(number)) return "Discount is required";
      if (number < 0 || number > 100)
        return "Discount must be between 0 and 100";
      return "";
    }

    if (name === "passType") {
      const text = String(value || "").trim();
      if (!text) return "Pass type is required";
      return "";
    }

    if (name === "applicablePasses") {
      const list = Array.isArray(value) ? value : [];
      if (list.length === 0) return "Please select at least one applicable pass";
      return "";
    }

    return "";
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    let finalValue: string | number = value;
    if (name === "discount") {
      if (value === "") {
        finalValue = "";
      } else {
        const num = Number(value);
        if (!isNaN(num)) {
          finalValue = Math.round(num);
        }
      }
    }
    setFormData((prev) => ({ ...prev, [name]: finalValue }));
    setErrors((prev) => ({ ...prev, [name]: validateField(name, finalValue) }));
  };

  const handleSubmit = async () => {
    const nextErrors: Record<string, string> = {
      offerName: validateField("offerName", formData.offerName),
      offerCode: validateField("offerCode", formData.offerCode),
      validFrom: validateField("validFrom", formData.validFrom),
      validTill: validateField("validTill", formData.validTill),
      discount: validateField("discount", formData.discount),
      passType: validateField("passType", formData.passType),
      applicablePasses:
        activePassOptions.length === 0 && formData.passType
          ? "No passes available for this type — please create a pass first"
          : validateField("applicablePasses", formData.applicablePasses),
    };

    const hasError = Object.values(nextErrors).some(Boolean);
    setErrors(nextErrors);
    if (hasError) return;

    try {
      const inputToSubmit = {
        ...formData,
        discount: Number(formData.discount),
      };
      if (mode === "edit" && id) {
        const { data } = await updateOffer({
          variables: { id, input: inputToSubmit },
        });
        if (!data?.updateOffer?.success) {
          setSnackbarMessage(
            data?.updateOffer?.message || "Failed to update offer",
          );
          setSnackbarType("error");
          setSnackbarOpen(true);
          return;
        }
        setSnackbarMessage(
          data.updateOffer.message || "Offer updated successfully",
        );
        setSnackbarType("success");
        setSnackbarOpen(true);
      } else {
        const { data } = await createOffer({ variables: { input: inputToSubmit } });
        if (!data?.createOffer?.success) {
          setSnackbarMessage(
            data?.createOffer?.message || "Failed to create offer",
          );
          setSnackbarType("error");
          setSnackbarOpen(true);
          return;
        }
        setSnackbarMessage(
          data.createOffer.message || "Offer created successfully",
        );
        setSnackbarType("success");
        setSnackbarOpen(true);
      }

      if (onSuccess) {
        setTimeout(onSuccess, 900);
      }
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Box display="flex" flexDirection="column" gap={3}>
      <Box
        sx={{
          backgroundColor: themestyle.colors.white,
          borderRadius: "20px",
          border: "1px solid #E7EEDC",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            backgroundColor: themestyle.colors.lightGreenBg,
            px: 3,
            py: 2.5,
          }}
        >
          <Typography
            fontSize="20px"
            fontWeight={700}
            color={themestyle.colors.activeGreen}
            fontFamily={themestyle.fontFamily.Roboto}
          >
            Offer Information
          </Typography>
        </Box>

        <Box
          p={3}
          display="flex"
          flexDirection="column"
          gap={3}
          sx={{ px: { xs: 2, sm: 3, md: 5 } }}
        >
          <Box
            display="flex"
            gap={3}
            flexDirection={{ xs: "column", md: "row" }}
          >
            <Box flex={1}>
              <FormLabel>Offer Name *</FormLabel>
              <TextField
                fullWidth
                sx={textFieldSX}
                name="offerName"
                value={formData.offerName}
                placeholder="eg., Summer Special 2026"
                onChange={handleTextChange}
                error={!!errors.offerName}
                helperText={errors.offerName}
                disabled={isViewMode}
              />
            </Box>
            <Box flex={1}>
              <FormLabel>Offer Code *</FormLabel>
              <TextField
                fullWidth
                sx={textFieldSX}
                name="offerCode"
                value={formData.offerCode}
                placeholder="Summer 50"
                onChange={handleTextChange}
                error={!!errors.offerCode}
                helperText={errors.offerCode}
                disabled={isViewMode}
              />
            </Box>
          </Box>

          <Box
            display="flex"
            gap={3}
            flexDirection={{ xs: "column", md: "row" }}
          >
            <Box flex={1}>
              <FormLabel>Valid From *</FormLabel>
              <AppDatePicker
                value={formData.validFrom}
                onChange={(v) => {
                  setFormData((prev) => ({ ...prev, validFrom: v }));
                  setErrors((prev) => ({
                    ...prev,
                    validFrom: validateField("validFrom", v),
                  }));
                }}
                error={!!errors.validFrom}
                helperText={errors.validFrom}
                disabled={isViewMode}
                minDate={currentGlobalDate}
              />
            </Box>
            <Box flex={1}>
              <FormLabel>Valid Till *</FormLabel>
              <AppDatePicker
                value={formData.validTill}
                onChange={(v) => {
                  setFormData((prev) => ({ ...prev, validTill: v }));
                  setErrors((prev) => ({
                    ...prev,
                    validTill: validateField("validTill", v),
                  }));
                }}
                error={!!errors.validTill}
                helperText={errors.validTill}
                disabled={isViewMode}
                minDate={formData.validFrom || currentGlobalDate}
              />
            </Box>
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          backgroundColor: themestyle.colors.white,
          borderRadius: "20px",
          border: "1px solid #E7EEDC",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            backgroundColor: themestyle.colors.lightGreenBg,
            px: 3,
            py: 2.5,
          }}
        >
          <Typography
            fontSize="20px"
            fontWeight={700}
            color={themestyle.colors.activeGreen}
            fontFamily={themestyle.fontFamily.Roboto}
          >
            Discount & Eligibility
          </Typography>
        </Box>

        <Box
          p={3}
          display="flex"
          flexDirection="column"
          gap={3}
          sx={{ px: { xs: 2, sm: 3, md: 5 } }}
        >
          <Box
            display="flex"
            gap={3}
            flexDirection={{ xs: "column", md: "row" }}
          >
            <Box flex={1}>
              <FormLabel>Discount (%) *</FormLabel>
              <TextField
                fullWidth
                sx={textFieldSX}
                name="discount"
                type="number"
                value={formData.discount}
                placeholder="Enter Discount"
                onChange={handleTextChange}
                error={!!errors.discount}
                helperText={errors.discount}
                disabled={isViewMode}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography sx={{ fontWeight: 600, color: "#666" }}>
                        %
                      </Typography>
                    </InputAdornment>
                  ),
                }}
                inputProps={{ min: 0, max: 100, step: 1 }}
              />
            </Box>
            <Box flex={1}>
              <FormLabel>Applicable Pass Type *</FormLabel>
              <Autocomplete
                options={PASS_TYPE_OPTIONS}
                value={
                  PASS_TYPE_OPTIONS.find(
                    (item) => item.value === formData.passType,
                  ) || null
                }
                getOptionLabel={(option) => option.label}
                onChange={(_, value) => {
                  if (!value) return;
                  setFormData((prev) => ({
                    ...prev,
                    passType: value.value,
                    applicablePasses: [],
                  }));
                  setErrors((prev) => ({
                    ...prev,
                    passType: "",
                    applicablePasses: "",
                  }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    sx={textFieldSX}
                    error={!!errors.passType}
                    helperText={errors.passType}
                  />
                )}
                disabled={isViewMode}
              />

              <Box
                sx={{
                  mt: 1.5,
                  border: "1px solid #D8E2CF",
                  borderRadius: "10px",
                  p: 1.5,
                  minHeight: 52,
                }}
              >
                {activePassOptions.length === 0 && formData.passType ? (
                  <Box
                    display="flex"
                    alignItems="center"
                    gap={1.5}
                    flexWrap="wrap"
                  >
                    <Typography sx={{ fontSize: 13, color: "#64748B" }}>
                      No active passes available for this type.
                    </Typography>
                    {!isViewMode && (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={() => navigate("/passes/add")}
                        sx={{
                          borderRadius: "8px",
                          borderColor: "#6CBC44",
                          color: "#6CBC44",
                          fontSize: 13,
                          fontWeight: 600,
                          textTransform: "none",
                          py: 0.5,
                          px: 1.5,
                          "&:hover": {
                            borderColor: "#5aaa35",
                            backgroundColor: "#F0F8E8",
                          },
                        }}
                      >
                        Create Pass
                      </Button>
                    )}
                  </Box>
                ) : (
                  <Box display="flex" flexWrap="wrap" gap={1}>
                    {activePassOptions.map((option: PassOption) => {
                      const selected = formData.applicablePasses.includes(option.name);
                      return (
                        <Chip
                          key={option.name}
                          label={
                            option.name.length > 40
                              ? `${option.name.slice(0, 40)}...`
                              : option.name
                          }
                          icon={
                            <Checkbox
                              checked={selected}
                              sx={{
                                p: 0,
                                ml: 0.5,
                                color: "#7CB342",
                                "&.Mui-checked": { color: "#7CB342" },
                              }}
                            />
                          }
                          onClick={() => {
                            if (isViewMode) return;
                            setFormData((prev) => ({
                              ...prev,
                              applicablePasses: selected
                                ? prev.applicablePasses.filter(
                                    (item) => item !== option.name,
                                  )
                                : [...prev.applicablePasses, option.name],
                            }));
                            setErrors((prev) => ({
                              ...prev,
                              applicablePasses: "",
                            }));
                          }}
                          sx={{
                            height: "36px",
                            borderRadius: "8px",
                            border: selected
                              ? "1px solid #8BC34A"
                              : "1px solid #E2E8F0",
                            backgroundColor: selected ? "#F0F8E8" : "#fff",
                            "& .MuiChip-root": {
                              maxWidth: "100%",
                            },
                            "& .MuiChip-label": {
                              fontSize: "14px",
                              fontWeight: 500,
                            },
                          }}
                          title={option.name}
                        />
                      );
                    })}
                  </Box>
                )}
                {!!errors.applicablePasses && (
                  <Typography
                    sx={{ mt: 1, color: "#d32f2f", fontSize: "12px" }}
                  >
                    {errors.applicablePasses}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>

          {selectedPassDetails.length > 0 && (
            <Box
              sx={{
                mt: 2,
                border: "1px solid #D8E2CF",
                borderRadius: "12px",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  backgroundColor: themestyle.colors.lightGreenBg,
                  px: 2.5,
                  py: 1.5,
                }}
              >
                <Typography
                  fontSize="14px"
                  fontWeight={700}
                  color={themestyle.colors.activeGreen}
                  fontFamily={themestyle.fontFamily.Roboto}
                >
                  Price Summary
                </Typography>
              </Box>
              {selectedPassDetails.map((pass: PassOption, idx: number) => {
                const originalCost = Number(pass.cost) || 0;
                const discountPct = Number(formData.discount) || 0;
                const discountAmt = parseFloat(
                  ((originalCost * discountPct) / 100).toFixed(2),
                );
                const finalPrice = parseFloat(
                  (originalCost - discountAmt).toFixed(2),
                );
                const fmt = (val: number) =>
                  new Intl.NumberFormat("en-IN", {
                    style: "currency",
                    currency: "INR",
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  }).format(val);
                return (
                  <Box
                    key={pass.name}
                    sx={{
                      px: 2.5,
                      py: 1.5,
                      borderTop: idx > 0 ? "1px solid #E7EEDC" : "none",
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" },
                      alignItems: { xs: "flex-start", sm: "center" },
                      gap: 2,
                    }}
                  >
                    <Typography
                      fontSize="14px"
                      fontWeight={600}
                      sx={{ flex: 1, color: "#1F2937" }}
                    >
                      {pass.name}
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        flexWrap: "wrap",
                      }}
                    >
                      <Box sx={{ textAlign: "center" }}>
                        <Typography
                          fontSize="11px"
                          color="#6B7280"
                          fontWeight={500}
                        >
                          Original Price
                        </Typography>
                        <Typography
                          fontSize="14px"
                          fontWeight={600}
                          color="#1F2937"
                        >
                          {fmt(originalCost)}
                        </Typography>
                      </Box>
                      {discountPct > 0 && (
                        <>
                          <Box
                            sx={{
                              px: 1.5,
                              py: 0.5,
                              borderRadius: "20px",
                              backgroundColor: "#FFF3E0",
                              border: "1px solid #FFB74D",
                            }}
                          >
                            <Typography
                              fontSize="13px"
                              fontWeight={700}
                              color="#E65100"
                            >
                              -{discountPct}%
                            </Typography>
                          </Box>
                          <Box sx={{ textAlign: "center" }}>
                            <Typography
                              fontSize="11px"
                              color="#6B7280"
                              fontWeight={500}
                            >
                              Discount
                            </Typography>
                            <Typography
                              fontSize="14px"
                              fontWeight={600}
                              color="#E53935"
                            >
                              -{fmt(discountAmt)}
                            </Typography>
                          </Box>
                          <Box sx={{ textAlign: "center" }}>
                            <Typography
                              fontSize="11px"
                              color="#6B7280"
                              fontWeight={500}
                            >
                              Final Price
                            </Typography>
                            <Typography
                              fontSize="15px"
                              fontWeight={700}
                              color={themestyle.colors.activeGreen}
                            >
                              {fmt(finalPrice)}
                            </Typography>
                          </Box>
                        </>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          px: isMobile ? 1 : { sm: 3, md: 5 },
          pb: 3,
          display: "flex",
          justifyContent: "flex-end",
          gap: isMobile ? 1 : 2,
        }}
      >
        <Button
          variant="outlined"
          onClick={onCancel}
          sx={{
            borderColor: themestyle.colors.buttonGreen,
            color: themestyle.colors.black,
            textTransform: "none",
            fontWeight: 600,
            px: isMobile ? 1.5 : 2,
            borderRadius: "8px",
            height: isMobile ? "36px" : "44px",
            fontSize: isMobile ? "13px" : "16px",
            minWidth: isMobile ? "75px" : "110px",
            "&:hover": {
              borderColor: themestyle.colors.buttonGreen,
            },
          }}
        >
          Cancel
        </Button>

        {mode === "view" && id ? (
          <>
            <Button
              variant="contained"
              onClick={onEdit}
              sx={{
                backgroundColor: themestyle.colors.buttonGreen,
                textTransform: "none",
                fontWeight: 600,
                whiteSpace: "nowrap",
                px: isMobile ? 1.5 : 5,
                height: isMobile ? "36px" : "44px",
                fontSize: isMobile ? "13px" : "16px",
                minWidth: isMobile ? "95px" : "110px",
                borderRadius: "8px",
                "&:hover": {
                  backgroundColor: themestyle.colors.buttonGreenHover,
                },
              }}
            >
              Edit
            </Button>
          </>
        ) : (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
            sx={{
              backgroundColor: themestyle.colors.buttonGreen,
              textTransform: "none",
              fontWeight: 600,
              whiteSpace: "nowrap",
              px: isMobile ? 1.5 : 5,
              height: isMobile ? "36px" : "44px",
              fontSize: isMobile ? "13px" : "16px",
              minWidth: isMobile ? "85px" : "110px",
              borderRadius: "8px",
              "&:hover": {
                backgroundColor: themestyle.colors.buttonGreenHover,
              },
              "&.Mui-disabled": { backgroundColor: "#A5D68B", color: "#fff" },
            }}
          >
            {mode === "edit" ? "Update Offer" : "Add Offer"}
          </Button>
        )}
      </Box>

      <CustomSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        type={snackbarType}
        onClose={() => setSnackbarOpen(false)}
      />
    </Box>
  );
};

export default AddOfferForm;
